import "../dashboard.css";
import StatsCardAccentImage from "@/assets/images/stats-card-accent.png";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type { StatusTranslation } from "@/utils/translations";
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	type ChipVariant,
	Icon,
	Skeleton,
	TableCell,
	TableRow,
	Typography,
} from "@st-marketplace/ui";
import { Link } from "@tanstack/react-router";
import type { ComponentProps, FC, ReactNode } from "react";

type IconsName = ComponentProps<typeof Icon>["name"];

export const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

/** One number, one label, one icon. Nothing else. */
export const StatTile: FC<{
	label: string;
	value: string;
	icon?: IconsName;
	hint?: string;
	loading?: boolean;
	className?: string;
}> = ({ label, value, icon, hint, loading, className }) => (
	<Card
		className={cn(
			"dashboard-stat relative overflow-hidden rounded-xl border border-secondary-border bg-background-50 p-4 sm:p-6",
			className,
		)}
	>
		<img
			src={StatsCardAccentImage}
			alt=""
			className="pointer-events-none absolute -top-1 -left-1 size-32 object-cover"
		/>
		<div className="relative flex items-center justify-between gap-2">
			<div className="min-w-0 flex-1 space-y-2 text-start">
				<Typography variant="body-md" className="mb-2 text-secondary-text-100">
					{label}
				</Typography>
				{loading ? (
					<Skeleton className="h-8 w-32" />
				) : (
					<Typography
						variant="title-md"
						as="p"
						className="break-words tabular-nums"
					>
						{value}
					</Typography>
				)}
				{hint ? (
					<Typography variant="body-sm" as="p" className="text-secondary-icon">
						{hint}
					</Typography>
				) : null}
			</div>
			{icon ? (
				<span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-background-400 text-secondary-text sm:size-12">
					<Icon name={icon} size="lg" />
				</span>
			) : null}
		</div>
	</Card>
);

/** A titled band of cards: icon + title on one side, controls on the other. */
export const DashboardSection: FC<{
	title: string;
	icon?: IconsName;
	trailing?: ReactNode;
	children: ReactNode;
}> = ({ title, icon, trailing, children }) => (
	<section className="flex min-w-0 flex-col gap-4">
		<div className="flex flex-wrap items-center justify-between gap-3">
			<Typography
				variant="title-sm"
				as="h2"
				className="flex flex-wrap items-center gap-2"
			>
				{icon ? (
					<Icon name={icon} size="sm" className="text-primary-icon" />
				) : null}
				{title}
			</Typography>
			{trailing}
		</div>
		{children}
	</section>
);

export const SectionCard: FC<{
	title: string;
	/** Optional headline figure shown under the title. */
	metric?: ReactNode;
	viewAllTo?: string;
	action?: ReactNode;
	children: ReactNode;
	className?: string;
}> = ({ title, metric, viewAllTo, action, children, className }) => {
	const { t } = useTranslation();
	return (
		<Card
			className={cn(
				"dashboard-section-card flex min-w-0 flex-col overflow-hidden",
				className,
			)}
		>
			<CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 border-b border-secondary-border">
				<div className="flex min-w-0 flex-col">
					<Typography variant="title-sm" as="h2">
						{title}
					</Typography>
					{metric}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{viewAllTo ? (
						<Link to={viewAllTo}>
							<Button type="button" variant="text" color="primary" size="sm">
								{t("home.viewAll")}
							</Button>
						</Link>
					) : null}
					{action}
				</div>
			</CardHeader>
			<CardContent className="min-w-0 flex-1 p-0">{children}</CardContent>
		</Card>
	);
};

export const EmptyRow: FC<{ colSpan: number; children: ReactNode }> = ({
	colSpan,
	children,
}) => (
	<TableRow>
		<TableCell colSpan={colSpan} className="py-8 text-center">
			<Typography variant="body-sm" className="text-secondary-text-100">
				{children}
			</Typography>
		</TableCell>
	</TableRow>
);

export const LoadingRows: FC<{ columns: number; rows?: number }> = ({
	columns,
	rows = 3,
}) => (
	<>
		{Array.from({ length: rows }, (_, i) => i).map((row) => (
			<TableRow key={row}>
				{Array.from({ length: columns }, (_, i) => i).map((col) => (
					<TableCell key={col}>
						<Skeleton className="h-5 w-full" />
					</TableCell>
				))}
			</TableRow>
		))}
	</>
);

/** Wide tables reflow into labeled rows when their card becomes narrow. */
export const DashboardTable: FC<{ children: ReactNode; compact?: boolean }> = ({
	children,
	compact,
}) => (
	<div className="dashboard-table-container">
		<table
			className={cn(
				"dashboard-table",
				compact ? "dashboard-table-compact" : "dashboard-table-wide",
			)}
		>
			{children}
		</table>
	</div>
);
